/**
 * Which chart cards are subscribed to each urql request.
 *
 * Needed because urql deduplicates identical (query, variables) pairs in
 * `cacheExchange`, which sits BEFORE the recorder — so one recorded operation
 * can be feeding several cards while the exchange only ever sees one. The
 * cards register themselves from `useUrqlQueryWithStatus`, which is the single
 * wrapper they all go through.
 *
 * Keyed by urql's own request key so it lines up exactly with `operation.key`.
 */
const chartItemIdsByRequestKey = new Map<number, Set<string>>();

export const registerChartRequest = (
  requestKey: number,
  chartItemId: string
) => {
  const existing = chartItemIdsByRequestKey.get(requestKey);
  if (existing) {
    existing.add(chartItemId);
    return;
  }
  chartItemIdsByRequestKey.set(requestKey, new Set([chartItemId]));
};

export const unregisterChartRequest = (
  requestKey: number,
  chartItemId: string
) => {
  const existing = chartItemIdsByRequestKey.get(requestKey);
  if (!existing) return;
  existing.delete(chartItemId);
  if (existing.size === 0) chartItemIdsByRequestKey.delete(requestKey);
};

export const getChartItemIdsForRequest = (requestKey: number): string[] => [
  ...(chartItemIdsByRequestKey.get(requestKey) ?? []),
];

/**
 * When each chart card last finished loading — the moment its data was on
 * screen. A card redraws on every filter change, so the recent stamps are
 * kept rather than the last one: a record matches the first draw at or after
 * its own response.
 */
const drawTimesByChartItemId = new Map<string, number[]>();
const MAX_DRAW_TIMES = 20;

export const markChartDrawn = (chartItemId: string, at: number) => {
  const times = drawTimesByChartItemId.get(chartItemId) ?? [];
  times.push(at);
  drawTimesByChartItemId.set(chartItemId, times.slice(-MAX_DRAW_TIMES));
};

export const getChartDrawTimes = (chartItemId: string): number[] => [
  ...(drawTimesByChartItemId.get(chartItemId) ?? []),
];
